import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PricingSection from './components/PricingSection';
import CoursesFaq from './components/CoursesFaq';
import TestimonialsSection from './components/TestimonialsSection';
import { Link } from 'react-router-dom';

function useVisible(threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const stages = [
  {
    step: '01',
    phase: '初學階段',
    duration: '約 3–6 個月',
    title: '認識聲音，建立安全感',
    desc: '從接觸古箏的聲音開始，不急著彈曲子。讓手指習慣琴弦，讓耳朵熟悉音色，讓身體自然放鬆。這個階段最重要的，是讓學習本身成為一件安心的事。',
    details: [
      '認識古箏結構與基本音位',
      '基礎撥弦姿勢與手型',
      '簡單旋律與節拍感培養',
      '建立課堂的安心感與節奏',
    ],
    emphasis: '沒有追趕進度的壓力，可以停留、可以反覆',
    color: 'bg-[#f5f0e8]',
    accent: '#c8a96e',
    img: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/eec1ccba-ea36-4384-ae56-a0e903685279_compressed_C--Image-202695-11_05_04.webp',
  },
  {
    step: '02',
    phase: '穩定練習階段',
    duration: '因人而異，無上限',
    title: '讓音樂走進日常',
    desc: '開始有了自己喜歡的曲目，練習逐漸成為日常習慣的一部分。這個階段不求完美，重視的是規律與感受——每次撥弦，都是與自己的對話。',
    details: [
      '挑選個人喜歡的曲目練習',
      '技巧逐步深化：搖指、輪指等',
      '音樂表達與個人詮釋探索',
      '建立屬於自己的練習節奏',
    ],
    emphasis: '音樂開始真正進入生活，而不是一項任務',
    color: 'bg-[#eaede5]',
    accent: '#4a6741',
    img: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/bf1af3c1-b4c0-4cb2-b1ac-0249ef91d51d_compressed_C--Image-202695-03_29_22.webp',
  },
  {
    step: '03',
    phase: '深化與調整階段',
    duration: '長期持續',
    title: '走出自己的聲音',
    desc: '技巧漸穩之後，開始探索自己的表達方式。不是為了表演，而是讓音樂成為你真正的語言。這個階段可以停留很久，因為走出自己的聲音，值得花時間。',
    details: [
      '個人化演奏風格的探索',
      '進階技巧與曲目深度詮釋',
      '音樂與情感表達的連結',
      '長期陪伴與定期回顧調整',
    ],
    emphasis: '沒有終點線，只有持續走得更深的路',
    color: 'bg-[#ede8e0]',
    accent: '#7a6248',
    img: 'https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/c8d1279d-4102-4b33-9c4a-497662ec4cec_compressed_C--Image-202695-11_24_24.webp',
  },
];

const principles = [
  { icon: 'ri-time-line', title: '沒有固定進度表', desc: '每個人的學習節奏不同，這裡不會設定「幾週內學完」的硬性目標。' },
  { icon: 'ri-loop-left-line', title: '可以停留與回顧', desc: '覺得某個階段想多練習？完全可以。反覆是深化的方式，不是退步。' },
  { icon: 'ri-heart-3-line', title: '重視感受與累積', desc: '每次課後你的感受，比進度更重要。安心的學習才能真正累積。' },
  { icon: 'ri-plant-line', title: '音樂融入生活', desc: '理想的狀態是，古箏逐漸成為生活的一部分，而不是另一種負擔。' },
];

export default function Courses() {
  const hero = useVisible(0.05);
  const stagesSection = useVisible();
  const principlesSection = useVisible();
  const cta = useVisible();

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 bg-[#f2ede5] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=guzheng%20music%20notes%20sheet%20on%20wooden%20desk%20with%20tea%20cup%2C%20warm%20natural%20morning%20light%2C%20minimal%20japanese%20aesthetic%2C%20muted%20cream%20tones&width=1920&height=700&seq=courses_hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div ref={hero.ref} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 transition-all duration-700 ${hero.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Courses</span>
          </div>
          <h1 className="font-serif-tc text-[#2c2420] leading-[1.3] mb-6" style={{ fontSize: 'clamp(2rem, 5vw, 2.75rem)' }}>
            課程與學習路徑
          </h1>
          <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] max-w-2xl mb-8">
            這裡沒有標準進度表，只有屬於你的節奏。每個階段都可以停留、調整、回看——重要的是你是否感到安心，而不是你走得有多快。
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#5a3e22] text-white text-[0.9375rem] font-medium hover:bg-[#3a2e22] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-price-tag-3-line"></i>
              查看費用說明
            </a>
            <a
              href="#courses-faq"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#c8b49a] bg-white text-[#5a4e42] text-[0.9375rem] font-medium hover:bg-[#f5f0e8] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-question-answer-line"></i>
              課程常見問題
            </a>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 md:py-24 bg-[#faf8f5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={principlesSection.ref} className={`transition-all duration-700 ${principlesSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {principles.map((p, i) => (
                <div
                  key={i}
                  className="bg-[#f5f0e8] rounded-2xl p-6 transition-all duration-700"
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#faf8f5] mb-4">
                    <i className={`${p.icon} text-[#7a6248] text-lg`}></i>
                  </div>
                  <h3 className="font-serif-tc text-[#2c2420] mb-2" style={{ fontSize: '1.0625rem' }}>{p.title}</h3>
                  <p className="text-[1rem] leading-[1.75] text-[#7a6e62]">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stages */}
      <section className="py-16 md:py-24 bg-[#f2ede5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={stagesSection.ref} className={`transition-all duration-700 ${stagesSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Learning Stages</span>
                <div className="divider-gold"></div>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4]" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                三個學習階段
              </h2>
              <p className="mt-4 text-[1.1875rem] leading-[1.75] text-[#7a6e62] max-w-xl mx-auto">
                不是非得按順序走，也不是走完就畢業。<br className="hidden md:block" />
                每個人的路徑都不一樣。
              </p>
            </div>

            <div className="space-y-8">
              {stages.map((stage, i) => (
                <div
                  key={i}
                  className={`${stage.color} rounded-2xl overflow-hidden transition-all duration-700`}
                  style={{ transitionDelay: `${i * 0.15}s` }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className={`relative ${i % 2 === 1 ? 'lg:order-2' : ''}`} style={{ minHeight: '280px' }}>
                      <img
                        src={stage.img}
                        alt={stage.title}
                        className="w-full h-full object-cover object-top absolute inset-0"
                      />
                    </div>
                    <div className={`p-8 md:p-12 flex flex-col justify-center ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span
                          className="text-[3rem] font-bold leading-none select-none"
                          style={{ color: stage.accent, opacity: 0.2 }}
                        >
                          {stage.step}
                        </span>
                        <div>
                          <span
                            className="inline-block px-3 py-1 rounded-full text-[0.8125rem] font-medium mb-1"
                            style={{ backgroundColor: `${stage.accent}20`, color: stage.accent }}
                          >
                            {stage.phase}
                          </span>
                          <p className="text-[0.875rem] text-[#9a8470]">{stage.duration}</p>
                        </div>
                      </div>
                      <h3 className="font-serif-tc text-[#2c2420] mb-4 leading-[1.45]" style={{ fontSize: '1.625rem' }}>
                        {stage.title}
                      </h3>
                      <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] mb-6">{stage.desc}</p>
                      <ul className="space-y-2 mb-6">
                        {stage.details.map((d, j) => (
                          <li key={j} className="flex items-center gap-2.5">
                            <div className="w-4 h-4 flex items-center justify-center shrink-0">
                              <i className="ri-checkbox-blank-circle-line text-[0.5rem]" style={{ color: stage.accent }}></i>
                            </div>
                            <p className="text-[1rem] leading-[1.6] text-[#5a4e42]">{d}</p>
                          </li>
                        ))}
                      </ul>
                      <div
                        className="inline-block px-4 py-2 rounded-lg text-[0.9375rem] italic"
                        style={{ backgroundColor: `${stage.accent}15`, color: stage.accent }}
                      >
                        ✦ {stage.emphasis}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Anchor CTA — 引導到費用說明 */}
      <section className="py-10 md:py-14 bg-[#f2ede5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-3" style={{ fontSize: 'clamp(1.25rem, 2.5vw, 1.625rem)' }}>
              想先知道哪一種課程適合你？
            </h3>
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 text-[1.0625rem] font-medium text-[#7a6248] hover:text-[#5a3e22] transition-colors cursor-pointer whitespace-nowrap"
            >
              查看費用與體驗方式
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-down-line text-sm"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <PricingSection />
      <CoursesFaq />

      {/* CTA */}
      <section className="py-24 md:py-28 bg-[#faf8f5]">
        <div ref={cta.ref} className={`w-full px-6 md:px-12 lg:px-20 text-center transition-all duration-700 ${cta.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-6" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
            不確定從哪個階段開始？
          </h2>
          <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] max-w-lg mx-auto mb-10">
            加入 LINE@ 聊聊你的狀況，我會幫你一起評估最適合的起點。不需要提前做功課，現在的你就是最好的開始。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="https://line.me" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#4a3e32] text-[#f5f0e8] text-[1rem] font-medium hover:bg-[#3a2e22] transition-all duration-300 cursor-pointer whitespace-nowrap">
              <i className="ri-line-fill text-[#06C755]"></i>
              加入 LINE@ 諮詢
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-[#7a6248] text-[#7a6248] text-[1rem] font-medium hover:bg-[#7a6248] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap">
              前往聯絡頁
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
